<template>
  <TModalNormal :open="show" @close="closeInviteModal">
    <div v-if="inviteStep === 1">
      <TH1
        >{{
          $t('course_settings.collaboration.invite.title', [
            {
              id: 'courseName',
              value: props.course.name,
            },
          ])
        }}
      </TH1>
      <TP2>{{ $t('course_settings.collaboration.invite.description') }}</TP2>
      <CourseSettingsCollaborationRoleSelector v-model="selectedInviteRole" />
      <TButtonFull class="mt-6" @click="selectInviteRole()">{{
        $t('generic_next')
      }}</TButtonFull>
    </div>
    <div v-if="inviteStep === 2">
      <TH1 class="mb-4 flex items-center">
        <TIcon
          name="arrow_back"
          class="mr-2 cursor-pointer text-primary-500"
          @click="goBackToRoleSelect()"
        />
        {{
          $t('course_settings.collaboration.invite.role_title', [
            {
              id: 'roleName',
              value: $t(
                `course_settings.collaboration.role.${selectedInviteRole.toLowerCase()}`,
              ),
            },
            {
              id: 'courseName',
              value: props.course.name,
            },
          ])
        }}
      </TH1>
      <div class="rounded-md border border-neutral-500 bg-white-500 p-4">
        <TH2> {{ $t('course_settings.collaboration.invite.link.title') }}</TH2>
        <TPCaption class="text-neutral-500"
          >{{ $t('course_settings.collaboration.invite.link.description') }}
        </TPCaption>
        <TLoadingIndicatorSmall v-if="linkGenerationLoading" />
        <TFormLinkCopy class="mt-4" :link="newInviteLink" v-else />
      </div>
      <div class="mt-4 rounded-md border border-neutral-500 bg-white-500 p-4">
        <TH2>
          {{ $t('course_settings.collaboration.invite.search.title') }}
        </TH2>
        <TPCaption class="text-neutral-500"
          >{{ $t('course_settings.collaboration.invite.search.description') }}
        </TPCaption>
        <input
          type="text"
          class="mt-4 block w-full appearance-none rounded-md border-b border-neutral-200 px-3 py-2 text-sm placeholder:text-neutral-500 focus:outline-hidden focus:ring-0"
          :placeholder="
            $t('course_settings.collaboration.invite.search.placeholder')
          "
          ref="searchBox"
          v-model="searchValue"
        />
        <div
          v-for="collaborator in foundCollaborators"
          class="mt-2 flex items-center justify-between rounded-md bg-neutral-50 p-2 px-4"
        >
          <TP1>{{ collaborator.fullName }}</TP1>
          <TButtonInline
            @click="addCollaborator(collaborator)"
            :loading="isLoading(collaborator)"
            v-if="!isInCourse(collaborator)"
            >{{ $t('course_settings.collaboration.invite.search.cta') }}
          </TButtonInline>
          <TIcon name="check_circle" class="text-success-500" v-else />
        </div>
      </div>
    </div>
  </TModalNormal>
</template>
<script lang="ts" setup>
import {
  type Course,
  type CourseCollaborator,
  CourseMembershipRole,
  type TenantMember,
} from '~/graphql';
import { urlForCourseInvite } from '~/utils/constants';
import toast from '~/components/Toast';
import { useAddCollaboratorMutation } from '~/composables';
import debounce from 'lodash/debounce';

const { $t } = useNuxtApp();

const props = defineProps<{
  course: Course;
  show: boolean;
}>();

const emits = defineEmits(['close']);

const inviteStep = ref(1);
const selectedInviteRole = ref(CourseMembershipRole.Teacher);
const newInviteLink = ref('');
const linkGenerationLoading = ref(false);

const { data, error, fetching } = useTenantMembersQuery();
const { data: courseData } = useCourseQuery({
  variables: {
    courseIdInput: {
      id: props.course.id,
    },
  },
});

function selectInviteRole() {
  inviteStep.value = 2;
  generateInviteLink();
}

function goBackToRoleSelect() {
  inviteStep.value = 1;
}

function closeInviteModal() {
  inviteStep.value = 1;
  emits('close');
}

const foundCollaborators = ref([]);
const searchValue = ref('');
const searchDebouncedValue = ref('');
watch(
  searchValue,
  debounce(() => {
    searchDebouncedValue.value = searchValue.value;
    const existingIds = [];
    if (data.value && data.value?.tenantMembers) {
      foundCollaborators.value = data.value.tenantMembers
        .sort((a, b) => a.fullName.localeCompare(b.fullName))
        .filter((collaborator) =>
          collaborator.fullName
            .toLowerCase()
            .includes(searchDebouncedValue.value.toLowerCase()),
        )
        .filter((collaborator) => {
          if (existingIds.includes(collaborator.userId)) {
            return false;
          }
          existingIds.push(collaborator.userId);
          return true;
        })
        .slice(0, 5);
    }
  }, 200),
  { immediate: true },
);

const createCourseCollaborationInvite =
  useCreateCourseCollaborationInviteMutation();

async function generateInviteLink() {
  linkGenerationLoading.value = true;
  const { data, error } = await createCourseCollaborationInvite.executeMutation(
    {
      createCourseCollaborationInput: {
        courseId: props.course.id,
        courseMembershipRole: selectedInviteRole.value,
      },
    },
  );
  if (error) {
    toast.error($t('generic_error'));
  } else {
    newInviteLink.value = urlForCourseInvite(
      data?.createCourseCollaborationInvite.courseInvite.code ?? '',
    );
  }
  linkGenerationLoading.value = false;
}

const addLoading = ref([]);
const courseMemberIds = ref([]);
const addCollaboratorMutation = useAddCollaboratorMutation();

watch(courseData, () => {
  courseMemberIds.value =
    courseData.value?.course?.collaborators?.map(
      (collaborator) => collaborator.userId,
    ) ?? [];
});

function isInCourse(collaborator: CourseCollaborator): boolean {
  return courseMemberIds.value.includes(collaborator.userId);
}

function isLoading(collaborator: CourseCollaborator): boolean {
  return addLoading.value.includes(collaborator.id);
}

async function addCollaborator(collaborator: TenantMember) {
  if (addLoading.value.includes(collaborator.id)) {
    return;
  }
  addLoading.value.push(collaborator.id);

  const { error } = await addCollaboratorMutation.executeMutation({
    addCourseCollaboratorInput: {
      courseId: props.course.id,
      userId: collaborator.userId,
      courseMembershipRole: selectedInviteRole.value,
    },
  });
  if (error) {
    toast.error($t('generic_error'));
  } else {
    toast.success($t('course_settings.collaboration.invite.search.success'));
    courseMemberIds.value.push(collaborator.userId);
  }
  addLoading.value = addLoading.value.filter((id) => id !== collaborator.id);
}
</script>
